import React, { useState, useEffect } from 'react';
import { ShoppingCart, Menu, X, Heart } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { navLinks } from '../mockData';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { useWishlist } from '../context/WishlistContext';
import LanguageSwitcher from './LanguageSwitcher';
import CurrencySwitcher from './CurrencySwitcher';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { getTotalItems, setIsCartOpen } = useCart();
  const { wishlist } = useWishlist();
  const { t } = useLanguage();
  const location = useLocation();
  const cartCount = getTotalItems();
  const wishlistCount = wishlist.length;

  // Səhifə dəyişəndə menyu avtomatik bağlansın
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname, location.hash]);

  // Menyu açıq olanda arxa fondakı səhifə sürüşməsin
  useEffect(() => {
    if (mobileMenuOpen) {
      const previous = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = previous;
      };
    }
  }, [mobileMenuOpen]);

  // Escape ilə bağlansın
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setMobileMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const renderNavLink = (link, isMobile = false) => {
    const isAnchor = link.href.startsWith('#');
    const computedHref = isAnchor ? `/${link.href}` : link.href;
    const isActive =
      location.pathname === link.href ||
      (isAnchor && location.pathname === '/' && location.hash === link.href);

    const desktopClass = `text-[13px] font-medium tracking-wider transition-colors duration-200 ${
      isActive ? 'text-black font-semibold' : 'text-gray-700 hover:text-black'
    }`;

    // Mobil linklər böyük toxunma sahəsi ilə
    const mobileClass = `block w-full px-6 py-4 text-base font-medium tracking-wide border-b border-gray-100 transition-colors ${
      isActive ? 'text-black font-semibold bg-gray-50' : 'text-gray-700 active:bg-gray-50'
    }`;

    const className = isMobile ? mobileClass : desktopClass;
    const handleClick = () => setMobileMenuOpen(false);

    if (isAnchor) {
      return (
        <a key={link.id} href={computedHref} onClick={handleClick} className={className}>
          {t(link.id)}
        </a>
      );
    }

    return (
      <Link key={link.id} to={computedHref} onClick={handleClick} className={className}>
        {t(link.id)}
      </Link>
    );
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 bg-white z-50 border-b border-gray-100">
        <div className="max-w-[1400px] mx-auto px-3 sm:px-6 py-2.5 sm:py-4">
          <div className="flex items-center justify-between gap-1 sm:gap-2">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0 block">
              <img
                src="/laliel-logo.png"
                alt="L'Aliel"
                className="h-7 sm:h-9 lg:h-10 w-auto object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              {navLinks.map((link) => renderNavLink(link, false))}
            </nav>

            {/* Sağ tərəf — valyuta və dil BÜTÜN ölçülərdə görünür */}
            <div className="flex items-center gap-0.5 sm:gap-2 lg:gap-4 flex-shrink-0">
              <a
                href="https://wa.me/994999737000"
                target="_blank"
                rel="noopener noreferrer"
                className="hidden lg:block px-6 py-2 border border-black text-black text-[13px] font-medium tracking-wider hover:bg-black hover:text-white transition-all duration-300"
              >
                {t('whatsapp')}
              </a>

              <CurrencySwitcher />
              <LanguageSwitcher />

              <Link
                to="/wishlist"
                aria-label="Wishlist"
                className="relative flex items-center justify-center w-10 h-10 sm:w-11 sm:h-11 hover:bg-gray-50 rounded transition-colors"
              >
                <Heart className="w-5 h-5 text-black" />
                {wishlistCount > 0 && (
                  <span className="absolute top-0.5 right-0.5 bg-red-500 text-white text-[10px] min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center">
                    {wishlistCount}
                  </span>
                )}
              </Link>

              <button
                type="button"
                onClick={() => setIsCartOpen(true)}
                aria-label="Səbət"
                className="relative flex items-center justify-center w-10 h-10 sm:w-11 sm:h-11 hover:bg-gray-50 rounded transition-colors"
              >
                <ShoppingCart className="w-5 h-5 text-black" />
                {cartCount > 0 && (
                  <span className="absolute top-0.5 right-0.5 bg-black text-white text-[10px] min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </button>

              {/* Burger */}
              <button
                type="button"
                onClick={() => setMobileMenuOpen((open) => !open)}
                aria-label="Menyu"
                aria-expanded={mobileMenuOpen}
                className="lg:hidden flex items-center justify-center w-10 h-10 sm:w-11 sm:h-11 hover:bg-gray-50 rounded transition-colors"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ---------- MOBİL PANEL ---------- */}

      {/* Arxa fon */}
      <div
        onClick={() => setMobileMenuOpen(false)}
        className={`lg:hidden fixed inset-0 bg-black z-[60] transition-opacity duration-300 ${
          mobileMenuOpen ? 'opacity-40 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        aria-hidden="true"
      />

      {/* Sürüşən panel */}
      <div
        className={`lg:hidden fixed top-0 right-0 bottom-0 w-[85%] max-w-sm bg-white z-[70] shadow-2xl flex flex-col transition-transform duration-300 ease-out ${
          mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        {/* Panel başlığı */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <img src="/laliel-logo.png" alt="L'Aliel" className="h-8 w-auto object-contain" />
          <button
            type="button"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Bağla"
            className="flex items-center justify-center w-11 h-11 -mr-2 hover:bg-gray-50 rounded transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Linklər */}
        <nav className="flex-1 overflow-y-auto overscroll-contain">
          {navLinks.map((link) => renderNavLink(link, true))}
        </nav>

        {/* Alt hissə — yalnız WhatsApp.
            Valyuta və dil header-dədir: panelin içində dropdown aşağı düşüb kəsilirdi. */}
        <div className="flex-shrink-0 border-t border-gray-100 px-6 py-5 bg-white">
          <a
            href="https://wa.me/994999737000"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setMobileMenuOpen(false)}
            className="block w-full px-6 py-3.5 bg-black text-white text-sm font-medium tracking-wider text-center rounded active:bg-gray-800 transition-colors"
          >
            WHATSAPP
          </a>
        </div>
      </div>
    </>
  );
};

export default Header;
